import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { PasswordInput } from '@/components/form/password-input';
import { useZodForm } from '@/components/form/use-zod-form';
import { requiredEmail } from '@/lib/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { login } from '../api/auth';
import { AuthCard } from '../components/auth-card';
import { useAuth } from '../use-auth';

const schema = z.object({
  email: requiredEmail,
  password: z.string().min(1, 'Informe a senha.'),
});

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isRestoring, applySession } = useAuth();
  const form = useZodForm(schema, { email: '', password: '' });
  const [serverError, setServerError] = useState<unknown>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (!isRestoring && user) return <Navigate to={from} replace />;

  const onSubmit = form.handleSubmit(async ({ email, password }) => {
    setServerError(null);
    try {
      const result = await login(email, password);

      // O desafio vai por `state` do router, e não pela URL: é credencial de curta duração,
      // e credencial em URL acaba no histórico do navegador e em log de proxy.
      if (result.challenge !== 'NONE') {
        const path = result.challenge === 'TOTP' ? '/entrar/codigo' : '/entrar/configurar-2fa';
        navigate(path, {
          replace: true,
          state: {
            challengeToken: result.challengeToken,
            trustDeviceDays: result.trustDeviceDays,
            from,
          },
        });
        return;
      }

      applySession(result);
      navigate(from, { replace: true });
    } catch (caught) {
      setServerError(caught);
    }
  });

  return (
    <AuthCard title="Entrar" description="Precificação e margem real para vendas em marketplaces.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError error={serverError} fallback="Não foi possível entrar." />

        <Field control={form.control} name="email" label="E-mail" required>
          {(field) => (
            // Sem `autoComplete` o gerenciador de senhas não reconhece o formulário.
            <Input {...field} type="email" autoComplete="username" />
          )}
        </Field>

        <Field control={form.control} name="password" label="Senha" required>
          {(field) => <PasswordInput {...field} autoComplete="current-password" />}
        </Field>

        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Entrando…' : 'Entrar'}
        </Button>

        <Link
          to="/esqueci-senha"
          className="block text-center text-sm text-muted-foreground hover:text-foreground"
        >
          Esqueci minha senha
        </Link>

        <Link
          to="/privacidade"
          className="block text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Política de privacidade
        </Link>
      </form>
    </AuthCard>
  );
}
